import React, { useRef } from "react";
import styled from "styled-components";
import { FormSectionElements, FormSubSections, ProgramContainer } from "../../entities/formTemplateDataType";
import { Heading3, spacing, TextParagraph } from "../../theme";
import { findAddressElement, getSubSectionButton } from "../../utilities/ElementUtils";
import { splitAddress } from "../../utilities/addressUtils";
import DataUtils from "../../utilities/DataUtils";
import colors from "../../theme/variables/colors";
import PasswordShowButtonCompnent from './PasswordShowButtonCompnent';
import PasswordHideButtonCompnent from './PasswordHideButtonCompnent';

const InfoElementContainer = styled.div`
    border-bottom: ${props => (props.className?.includes('notBordered') ? '0px' : '2px')} solid ${colors.palette.greyscale5};
    margin-top: ${spacing.spacing5};
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    align-items: start;
    gap: 1rem;

    @media (min-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    }
`;

const StyledHeading = styled(Heading3)`
    display: inline;
    white-space: break-spaces;
    overflow-wrap: break-word;
    word-break: break-word;
`;

const SplitRight = styled.div`
    display: flex;
    width: auto;
    justify-content: space-between;
    gap: 1rem;
    overflow: hidden;
    min-width: 0;

    @media (max-width: 480px) {
        flex-wrap: wrap;
    }
`;

const Text = styled(TextParagraph)`
    margin: ${spacing.spacing0} ${spacing.spacing0} ${spacing.spacing3} ${spacing.spacing0};
    word-break: break-all;
    overflow-wrap: break-word;
    min-width: 0;
`;

interface Props {
    field: FormSectionElements;
    isEng: boolean;
    withButton: boolean;
    handleEditButtonClick?: (field: FormSectionElements) => void;
    callback: (programContainer: ProgramContainer) => void;
    children?: string | React.ReactNode;
    hideChangeLink?: boolean;
}

const TableSummaryComponent: React.FC<Props> = ({
    field, isEng, withButton, handleEditButtonClick, callback, children, hideChangeLink
}: Props) => {
    const initialized = useRef(false);
    if (!initialized.current) {
        field.mask = true;
        initialized.current = true;
    }
    const splitAddressFromField = (summaryField: FormSectionElements) => {
        const addressField = findAddressElement(summaryField);
        if (addressField) {
            return splitAddress(addressField.Address)
        }
        return '';
    }
    const getValue = (field: FormSectionElements) => {
        let value = '';
        if (field.MaskInSummary) {
            switch (field.Datatype) {
                case "ODSDateRange":
                    if (field.mask) {
                        value = "****/**/** - ****/**/**";
                    } else if (field.Value) {
                        const parts = String(field.Value).split('|');
                        const startDate = parts[0] || '';
                        const endDate = parts[1] || '';
                        value = `${startDate || 'N/A'} - ${endDate || 'N/A'}`;
                    }
                    break;
                case "ODSDate":
                    value = field.mask ? "****/**/**" : field.Value;
                    break;
                case "Address":
                    // value= this.splitAddress((field.Address || this.profile?.address) as Address | undefined)
                    value = splitAddressFromField(field)
                    break;
                case "Email":
                    const emailParts = field.Value?.split('@');
                    let maskedValue = emailParts[0].substring(0, 2) + '******';
                    if (emailParts.length > 1) {
                        const emailDomainParts = emailParts[1].split('.');
                        maskedValue += `@${emailDomainParts[0].substring(0, 1)}*****`;
                        if (emailDomainParts.length > 1) {
                            maskedValue += `.${emailDomainParts[1]}`;
                        }
                    }
                    value = field.mask? maskedValue:field.Value;
                    break;
                default:
                    if (field.mask) {
                        const len = field.Value?.length;
                        if (len > 3) {
                            value = DataUtils.maskString(field.Value, 0, len - 3);
                        } else if (len) {
                            value = '****';
                        }
                    } else {
                        value = field.Value;
                    }
            }
        } else {
            value = field.Value;
        }
        return value;
    }

    return (
        <InfoElementContainer className={`info-element`}>
            <StyledHeading styledAs="h4">
                {isEng ? field.ENUTitle : field.FRATitle}
            </StyledHeading>
            <SplitRight id={`info-element-split-${field.Id}`}>
                <Text
                    dangerouslySetInnerHTML={{
                        __html: getValue(field) || '',
                    }}
                />{' '}
                {withButton && field?.MaskInSummary ?
                    field.mask
                        ? <PasswordShowButtonCompnent field={field} callback={(programContainer) => {
                            callback(programContainer);
                        }} />
                        : <PasswordHideButtonCompnent field={field} callback={(programContainer) => {
                            callback(programContainer);
                        }} /> : null}
                {!hideChangeLink && !field.parent.parent?.CustomizedSummaryProps?.changeLinkInSubsectionLevel && !field.parent.ReadOnlyElements &&
                    <button style={{display: 'flex', alignItems: 'start', border: 'none', backgroundColor: '#fff', cursor: 'pointer', textDecoration: 'underline', padding: '0.25rem 0'}} 
                        title={isEng ? "Edit" : "Modifier"}
                        onClick={() => {
                            const subSectionButton = getSubSectionButton(field.parent as FormSubSections);
                            if (subSectionButton)
                                handleEditButtonClick(subSectionButton);
                            else {
                                handleEditButtonClick(field);
                            }
                        }}>
                        <span style={{color: "#00478f", fontSize: "1.2rem"}}>{isEng ? "Change" : "Modifier"}</span>
                    </button>
                }
            </SplitRight>
            {children}
        </InfoElementContainer>
    );
}

export default TableSummaryComponent;